import styled from '@emotion/styled'
import Tab from '@mui/material/Tab'
import MaterialTabs from '@mui/material/Tabs'
import type React from 'react'
import { colors } from 'themes'
import Colors from '../../figma/panda/Colors'
import MediaQueries from '../../figma/tokens/MediaQueries'
import Spacings from '../../figma/tokens/Spacings'
import FigmaBox from '../components/FigmaBox'
import useActiveTabKey from '../hooks/useActiveTabKey'
import { PAGE_CONTENT_SIZE, TAB_PANEL_ITEM_HEIGHT_PX } from '../StrongHardCodedSizes'

export type TabType = {
  id: string
  labelElement: React.ReactElement
  contentElement: React.ReactElement
  enable?: boolean
  onClick?: () => unknown
}

type Props = {
  fullPage?: boolean
  alwaysShowNavHeadings?: boolean
  tabKeyNameSpace: string
  tabs: TabType[]
  onTabChange?: (activeIndex: number) => void
  tab?: number | null
}

/**
 * Mui Tabs with support for nested tabs and optional url param navigation
 *
 * @remarks
 *
 * @param fullPage - disable Gazelle max-width limit and use the whole page
 * @param alwaysShowNavHeadings - display tab controls even if we have just one tab
 * @param tabKeyNameSpace - namespace used for storing active tab in localStorage
 * @param tabs - array with label, content, and enable flag for each tab
 * @param onTabChange - callback to be executed after tab change
 *
 */

export default function Tabs({ fullPage, alwaysShowNavHeadings, tabKeyNameSpace, tabs: _tabs, onTabChange, tab }: Props) {
  const tabs = _tabs.filter((t) => t.enable !== false)
  const [rememberedTab, setRememberedTab] = useActiveTabKey(tabKeyNameSpace, tabs, '0')
  const activeTabIndex = tab ?? parseInt(rememberedTab)

  const visibleTabIndex = tabs.findIndex((t) => t.id === _tabs[activeTabIndex]?.id)

  if (tab === null && visibleTabIndex >= 0) {
    const visibleTab = tabs[visibleTabIndex]
    const tabIndexInOriginalTabs = _tabs.findIndex((t) => t.id === visibleTab.id)

    onTabChange?.(tabIndexInOriginalTabs)
  }

  // Change tab
  const handleChange = (index: number) => {
    const visibleTab = tabs[index]
    const tabIndexInOriginalTabs = _tabs.findIndex((t) => t.id === visibleTab.id)

    const onClick = tabs[index].onClick

    if (onClick) return onClick()

    setRememberedTab(tabIndexInOriginalTabs.toString())
    onTabChange?.(tabIndexInOriginalTabs)
  }

  // Show only tab content (without tab heading) if there's only one tab and alwaysShowNavHeadings is not enforced
  if (!alwaysShowNavHeadings && tabs.length === 1) return tabs[0].contentElement

  return (
    <Wrapper fullWidth fullPage={fullPage}>
      <MaterialTabs
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        value={visibleTabIndex}
        onChange={(event, newTab: number) => handleChange(newTab)}
        slotProps={{
          indicator: { style: { backgroundColor: colors.brand.blue.CTA, height: 2 } },
        }}
      >
        {tabs.map(({ labelElement }, index) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: ignore existing biome errors
          <Tab key={index} label={labelElement} />
        ))}
      </MaterialTabs>
      <Container fullWidth fullPage={fullPage}>
        {tabs.map(({ contentElement, onClick }, index) => {
          if (visibleTabIndex === index) onClick?.()

          return (
            // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
            <TabPanel activeTabIndex={visibleTabIndex} index={index} key={index}>
              {contentElement}
            </TabPanel>
          )
        })}
      </Container>
    </Wrapper>
  )
}

const Wrapper = styled(FigmaBox)<{ fullPage?: boolean }>`
  ${({ fullPage }) => !fullPage && `max-width: ${PAGE_CONTENT_SIZE};`}

  .MuiTabs-indicator {
    height: ${TAB_PANEL_ITEM_HEIGHT_PX};
    background-color: ${Colors.baseBlack};
  }

  .MuiTab-textColorInherit.Mui-selected {
    .MuiTab-wrapper {
      color: ${Colors.bluePrimary600Cta};
    }
  }

  .MuiTab-root {
    align-items: center;
    min-width: 0;
    width: auto;
    padding-left: 0;
    padding-right: 0;
    white-space: nowrap;

    :not(:last-child) {
      margin-right: ${Spacings.large};
    }
  }

  .Mui-selected span {
    opacity: 1;
    font-weight: 600 !important;
    color: ${Colors.baseBlack} !important;
  }

  .MuiTabs-indicator {
    min-width: 0;
  }

  .MuiTabs-scroller {
    @media (max-width: ${MediaQueries.md}px) {
      margin-left: ${Spacings.small};
      margin-right: ${Spacings.small};
    }
  }

  .MuiTabs-scrollButtons {
    opacity: 1 !important;
  }
`

const Container = styled(FigmaBox)<{ fullPage?: boolean }>`
  position: relative;
  ${({ fullPage }) => !fullPage && `max-width: ${PAGE_CONTENT_SIZE};`}
  margin-top: ${Spacings.medium};
`

function TabPanel({ index, activeTabIndex, children }: { activeTabIndex: number; index: number; children: React.ReactNode }) {
  return (
    <FigmaBox role="tabpanel" fullWidth hidden={activeTabIndex !== index}>
      {activeTabIndex === index && children}
    </FigmaBox>
  )
}
